import { z } from "zod";
import { Cents, Money } from "./money";

/**
 * Rates used to derive settlement and profit figures.
 *
 * FEE_RATE mirrors the commission shown on the admin dashboard.
 * THREE_DS / TXN values are the acquirer-side costs Atas Pay pays per transaction.
 */
export namespace AccountingRates {
  /** Commission charged to the merchant: 10% of the day's total. */
  export const FEE_RATE = 0.1;
  /** 3DS cost per 3DS-authenticated transaction: $0.20. */
  export const THREE_DS_COST_CENTS = 20;
  /**
   * Transaction fee is tiered by transaction share. Each transaction also pays a
   * flat fixed amount on top of its percentage rate. Because only the day's
   * aggregate total and count are known, each tier's share of the count is taken
   * to carry the same share of the total (uniform average transaction size), so
   * the percentage part reduces to a single blended rate on the total.
   */
  export const TXN_FEE_TIER_A_SHARE = 0.05; // 5% of transactions...
  export const TXN_FEE_TIER_A_RATE = 0.02; // ...at 2%
  export const TXN_FEE_TIER_B_SHARE = 0.1; // 10% of transactions...
  export const TXN_FEE_TIER_B_RATE = 0.0275; // ...at 2.75%
  /** The remaining transactions' rate: 1.75%. */
  export const TXN_FEE_RATE = 0.0175;
  /** Flat amount every transaction pays on top of its percentage rate: $0.30. */
  export const TXN_FEE_FIXED_CENTS = 30;
  /** Flat settlement fee charged once for each day that has a settlement. */
  export const SETTLEMENT_FEE_CENTS = 1000;
  /** Flat IT cost deducted once from the monthly net-profit total: $2,000. */
  export const MONTHLY_IT_COST_CENTS = 200000;
}

/**
 * The per-day accounting document shared by atas-pay-acc and atas-pay-admin.
 *
 * One document per date, covering all sites combined:
 *   /daily-accounting/{yyyy-MM-dd}
 *
 * Only the operator-entered figures are persisted — the disputes total and the
 * number of 3DS-authenticated transactions, neither of which can be derived from
 * the payment documents. Everything else (fees, net settlement, 3DS cost,
 * transaction fees, net profit) is derived from these plus the day's payments,
 * so the two apps cannot drift apart.
 */
export namespace DailyAccounting {
  export const schema = z.object({
    date: z.string(),
    disputes: Money.schema,
    /**
     * How many of the day's transactions went through 3DS. Entered by hand and
     * independent of the total transaction count — only some transactions are
     * 3DS-authenticated, so this is normally lower.
     */
    threeDsCount: z.number().int().nonnegative().optional(),
    updatedAt: z.string().optional(),
    updatedBy: z.string().optional(),
  });

  export type Type = z.infer<typeof schema>;

  export const path = (date: string) => `/daily-accounting/${date}`;

  /**
   * Whether anyone has affirmatively recorded the day's disputes.
   *
   * An absent document means "nobody has reviewed this day yet", which is NOT
   * the same as "there were no disputes" — so a recorded $0.00 counts as
   * recorded and a missing document does not. Net settlement is not meaningful
   * until this is true; both apps must show it as pending instead of quietly
   * settling with disputes treated as nil.
   */
  export const disputesRecorded = (record?: Type): boolean =>
    typeof record?.disputes?.amount === "number";
}

export type DailyAccounting = DailyAccounting.Type;

/**
 * All figures are in cents, in and out.
 *
 * Summing (1.75% * amount + $0.30) over each transaction is algebraically
 * identical to 1.75% * dayTotal + $0.30 * count, so the aggregate form is used
 * here — it works whether the count is auto-derived or typed in by hand.
 */
export namespace AccountingCalculations {
  /** Commission earned from the merchant: 10% of the day's total. */
  export const fees = (total: Cents): Cents =>
    Math.round(total * AccountingRates.FEE_RATE);

  /** What settles after our fees and any disputes are taken out. */
  export const netSettlement = (total: Cents, disputes: Cents): Cents =>
    total - fees(total) - disputes;

  /**
   * $0.20 per 3DS-authenticated transaction. Takes the hand-entered 3DS count,
   * NOT the total transaction count — only some transactions use 3DS.
   */
  export const threeDsCost = (threeDsCount: number): Cents =>
    threeDsCount * AccountingRates.THREE_DS_COST_CENTS;

  /**
   * Tiered percentage of the day's total (5% of txns at 2%, 7% at 2.75%, the
   * rest at 1.75%) plus a flat $0.30 per transaction. The tiered percentage
   * collapses to one blended rate on the total; the fixed part applies to every
   * transaction, so it uses the count derived from the payment documents.
   */
  export const transactionFees = (
    total: Cents,
    transactionCount: number,
  ): Cents => {
    const restShare =
      1 - AccountingRates.TXN_FEE_TIER_A_SHARE - AccountingRates.TXN_FEE_TIER_B_SHARE;
    const blendedRate =
      AccountingRates.TXN_FEE_TIER_A_SHARE * AccountingRates.TXN_FEE_TIER_A_RATE +
      AccountingRates.TXN_FEE_TIER_B_SHARE * AccountingRates.TXN_FEE_TIER_B_RATE +
      restShare * AccountingRates.TXN_FEE_RATE;
    return (
      Math.round(total * blendedRate) +
      transactionCount * AccountingRates.TXN_FEE_FIXED_CENTS
    );
  };

  /**
   * Flat $10 fee for each day that settles. Charged only when the day had
   * activity — a day with no transactions had no settlement, so no fee (this
   * also stops empty days from dragging a weekly/monthly total down by $10 each).
   */
  export const settlementFee = (transactionCount: number): Cents =>
    transactionCount > 0 ? AccountingRates.SETTLEMENT_FEE_CENTS : 0;

  /**
   * Fees earned, less the acquirer-side costs of earning them and the flat daily
   * settlement fee. Floored at $0 — a day's net profit is never reported as a
   * loss (in a period the sum of floored days is therefore also never negative).
   *
   * The two counts are independent: transactionCount comes from the payment
   * documents and drives the 1.75% + $0.30 (and whether the settlement fee
   * applies); threeDsCount is entered by hand and drives only the $0.20 3DS charge.
   */
  export const netProfit = (
    total: Cents,
    transactionCount: number,
    threeDsCount: number,
  ): Cents =>
    Math.max(
      0,
      fees(total) -
        threeDsCost(threeDsCount) -
        transactionFees(total, transactionCount) -
        settlementFee(transactionCount),
    );
}
